import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { CATEGORIES } from "../mock";
import { useProducts } from "../lib/useProducts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Slider } from "../components/ui/slider";
import { Filter, X } from "lucide-react";
import SEO from "../components/SEO";

const Shop = () => {
  const { items: PRODUCTS, loading } = useProducts();
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const q = (searchParams.get("q") || "").toLowerCase();

  const [sort, setSort] = useState("popularity");
  const [tags, setTags] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 300]);
  const [mobileFilter, setMobileFilter] = useState(false);

  useEffect(() => {
    setTags([]);
    setPriceRange([0, 300]);
  }, [category]);

  const allTags = Array.from(new Set(PRODUCTS.flatMap((p) => p.tags)));

  const products = useMemo(() => {
    let list = [...PRODUCTS];
    if (category) list = list.filter((p) => p.category === category);
    if (q) list = list.filter((p) => (p.name + p.short).toLowerCase().includes(q));
    if (tags.length > 0) list = list.filter((p) => p.tags.some((t) => tags.includes(t)));
    list = list.filter((p) => {
      const minPrice = Math.min(...p.variants.map((v) => v.price));
      return minPrice >= priceRange[0] && minPrice <= priceRange[1];
    });
    if (sort === "price-asc") list.sort((a, b) => Math.min(...a.variants.map((v) => v.price)) - Math.min(...b.variants.map((v) => v.price)));
    if (sort === "price-desc") list.sort((a, b) => Math.min(...b.variants.map((v) => v.price)) - Math.min(...a.variants.map((v) => v.price)));
    if (sort === "rating") list.sort((a, b) => b.rating - a.rating);
    return list;
  }, [category, q, sort, tags, priceRange, PRODUCTS]);

  const catName = category ? CATEGORIES.find((c) => c.slug === category)?.name || "Shop" : "All compounds";

  const FilterPanel = (
    <div className="space-y-8">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#b91c1c] mb-3">Categories</div>
        <ul className="space-y-1.5">
          <li>
            <Link to="/shop" className={`block py-1 text-[13px] hover:text-[#b91c1c] transition-colors ${!category ? "text-[#0a0a0a] font-semibold" : "text-gray-600"}`}>
              All Products
            </Link>
          </li>
          {CATEGORIES.map((c) => (
            <li key={c.slug}>
              <Link to={`/shop/${c.slug}`} className={`block py-1 text-[13px] hover:text-[#b91c1c] transition-colors ${category === c.slug ? "text-[#0a0a0a] font-semibold" : "text-gray-600"}`}>
                {c.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div className="pt-6 border-t border-gray-200">
        <div className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#b91c1c] mb-3">Filter By Tag</div>
        <div className="space-y-2">
          {allTags.map((t) => (
            <label key={t} className="flex items-center gap-2 cursor-pointer text-[13px] capitalize text-gray-600">
              <Checkbox
                checked={tags.includes(t)}
                onCheckedChange={(v) => setTags((prev) => v ? [...prev, t] : prev.filter((x) => x !== t))}
              />
              {t}
            </label>
          ))}
        </div>
      </div>
      <div className="pt-6 border-t border-gray-200">
        <div className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#b91c1c] mb-3">Price Range</div>
        <Slider min={0} max={300} step={5} value={priceRange} onValueChange={setPriceRange} className="my-4" />
        <div className="flex justify-between text-[12px] text-gray-500">
          <span>${priceRange[0]}</span>
          <span>${priceRange[1]}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-12">
      <SEO
        title={catName === "All compounds" ? "Shop — All Research Compounds" : `Shop — ${catName}`}
        description={`Browse Certified Wellness ${catName === "All compounds" ? "research compounds and blends" : catName.toLowerCase()}. HPLC-tested. COA on every batch. Made in the USA. For laboratory research use only.`}
        canonical={category ? `/shop/${category}` : "/shop"}
      />
      {/* Header */}
      <nav className="text-[11px] tracking-[0.18em] uppercase text-gray-400 mb-5">
        <Link to="/" className="hover:text-[#0a0a0a]">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/shop" className="hover:text-[#0a0a0a]">Shop</Link>
        {category && (<><span className="mx-2">/</span><span className="text-[#0a0a0a] font-semibold">{catName}</span></>)}
      </nav>

      <div className="flex items-end justify-between mb-10 pb-8 border-b border-gray-200">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="h-px w-8 bg-[#b91c1c]" />
            <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">Catalog</span>
          </div>
          <h1 className="font-display text-5xl md:text-6xl font-semibold text-[#0a0a0a] tracking-tight">
            {catName === "All compounds" ? (<>All <span className="font-serif italic font-normal">compounds.</span></>) : catName}
          </h1>
          <p className="text-gray-500 mt-3 text-sm">{products.length} {products.length === 1 ? "product" : "products"} • Lab tested • Made in USA</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-12">
        <aside className="hidden lg:block">{FilterPanel}</aside>

        <div>
          <div className="flex items-center justify-between mb-6 gap-2">
            <button onClick={() => setMobileFilter(true)} className="lg:hidden inline-flex items-center gap-2 border border-gray-300 px-3 py-2 text-[12px] font-semibold uppercase tracking-wider">
              <Filter className="h-4 w-4" /> Filters
            </button>
            <div className="hidden lg:block text-[12px] uppercase tracking-[0.18em] text-gray-400">
              {products.length} of {PRODUCTS.length} results
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-56 border-gray-300 rounded-none text-[12px] uppercase tracking-wider">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="popularity">Sort by popularity</SelectItem>
                <SelectItem value="rating">Sort by rating</SelectItem>
                <SelectItem value="price-asc">Price: low to high</SelectItem>
                <SelectItem value="price-desc">Price: high to low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {products.length === 0 ? (
            <div className="py-20 text-center text-gray-400 text-sm">
              No products match your filters.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-10">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>

      {mobileFilter && (
        <div className="fixed inset-0 z-50 bg-black/50 lg:hidden" onClick={() => setMobileFilter(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display font-semibold text-lg">Filters</h3>
              <button onClick={() => setMobileFilter(false)}><X className="h-5 w-5" /></button>
            </div>
            {FilterPanel}
          </div>
        </div>
      )}
    </div>
  );
};

export default Shop;
